import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaSync } from 'react-icons/fa';
import { verifyPayment } from '../../api/services/paymentService';

const PaymentVerificationModal = ({ show, onHide, orderId, onSuccess }) => {
    const [status, setStatus] = useState('idle'); // idle, loading, success, failure
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleVerify = async () => {
        if (!orderId) return;

        setStatus('loading');
        setError(null);
        try {
            const data = await verifyPayment(orderId);
            setResult(data);
            setStatus('success');
            if (onSuccess && data.status === 'SUCCESS') {
                onSuccess();
            }
        } catch (err) {
            console.error('Verification Error:', err);
            setError(err.response?.data?.message || err.message || 'Failed to verify payment');
            setStatus('failure');
        }
    };

    // Reset state when modal opens
    useEffect(() => {
        if (show && orderId) {
            handleVerify();
        } else if (!show) {
            setStatus('idle');
            setResult(null);
            setError(null);
        }
    }, [show, orderId]);

    const renderContent = () => {
        if (status === 'loading') {
            return (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <h5>Verifying Payment...</h5>
                    <p className="text-muted">Checking status with Cashfree for Order ID: <strong>{orderId}</strong></p>
                </div>
            );
        }

        if (status === 'success' && result) {
            const isPaid = result.status === 'SUCCESS';
            return (
                <div className="py-3">
                    <div className="text-center mb-4">
                        {isPaid ? (
                            <FaCheckCircle className="text-success mb-2" size={60} />
                        ) : (
                            <FaInfoCircle className="text-warning mb-2" size={60} />
                        )}
                        <h4 className={isPaid ? "text-success" : "text-warning"}>
                            {isPaid ? "Payment Verified Successfully!" : "Order Status: " + result.status}
                        </h4>
                    </div>

                    <div className="bg-light p-3 rounded">
                        <div className="d-flex justify-content-between mb-2">
                            <span>Order ID:</span>
                            <strong>{result.orderId}</strong>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                            <span>Customer:</span>
                            <strong>{result.fullName}</strong>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                            <span>Amount:</span>
                            <strong>₹{result.amount}</strong>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                            <span>Status:</span>
                            <span className={`badge ${isPaid ? 'bg-success' : 'bg-warning text-dark'}`}>
                                {result.status}
                            </span>
                        </div>
                        {result.paymentMethod && (
                            <div className="d-flex justify-content-between mb-2">
                                <span>Method:</span>
                                <strong>{result.paymentMethod}</strong>
                            </div>
                        )}
                        {result.paidAt && (
                            <div className="d-flex justify-content-between">
                                <span>Paid At:</span>
                                <strong>{new Date(result.paidAt).toLocaleString()}</strong>
                            </div>
                        )}
                    </div>

                    {!isPaid && (
                        <Alert variant="info" className="mt-3">
                            <FaInfoCircle className="me-2" />
                            The order is still in {result.status} state on Cashfree. No database updates were made.
                        </Alert>
                    )}
                </div>
            );
        }

        if (status === 'failure') {
            return (
                <div className="py-4">
                    <div className="text-center mb-4 text-danger">
                        <FaExclamationTriangle size={60} className="mb-2" />
                        <h4>Verification Failed</h4>
                    </div>
                    <Alert variant="danger">
                        {error}
                    </Alert>
                    <div className="text-center">
                        <Button variant="outline-danger" onClick={handleVerify}>
                            <FaSync className="me-2" /> Try Again
                        </Button>
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <Modal show={show} onHide={onHide} centered size="md">
            <Modal.Header closeButton className="border-0">
                <Modal.Title>Payment Verification</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-0">
                {renderContent()}
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PaymentVerificationModal;
